import { useState } from 'react'
import type { GoldenCaseSource } from '../lib/api.ts'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'
import { ScoreDiff } from './ScoreDiff.tsx'

/**
 * The score, beside a way back to the case it was measured against.
 *
 * One number and its parts. The parts matter more than the total when
 * something moves: an overall that drops because grouping broke is a
 * different problem from one that drops because a dose was misread, and a
 * single figure hides which.
 */
export function GoldenCaseScore({
  source,
  onOpenCase,
}: {
  readonly source: GoldenCaseSource
  readonly onOpenCase?: ((caseId: string) => void) | undefined
}) {
  const [diff, setDiff] = useState(false)
  const pct = (n: number) => `${Math.round(n * 100)}%`
  const score = source.score

  return (
    <div className="rounded-base border-stroke-brand-strong bg-fill-brand-weak/20 flex flex-wrap items-center gap-3 border p-3">
      <span className="text-tTiny text-text-weak font-semibold uppercase">
        Conjunto de referência
      </span>
      {onOpenCase === undefined ? (
        <span className="text-tP text-text-strong">{source.caseId}</span>
      ) : (
        <Button
          variant="tertiary"
          tone="brand"
          size="small"
          title="Abrir o caso do gabarito"
          onClick={() => onOpenCase(source.caseId)}
        >
          {source.caseId}
        </Button>
      )}

      <span className="grow" />

      {score === undefined ? (
        // Said rather than left blank: a missing score has a reason, and
        // "still a draft" is a different problem from "no label found".
        <span className="text-tSmall text-text-weak">
          Sem pontuação — {source.scoreSkipped ?? 'motivo desconhecido'}
        </span>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-tPSemi text-text-strong tabular-nums">{pct(score.overall)}</span>
          <span className="text-tSmall text-text-weak tabular-nums">
            ativos {pct(score.ingredients.f1)} · doses {pct(score.doses.f1)} · preparações{' '}
            {score.grouping.actual}/{score.grouping.expected}
            {/* Only where the label statuses them, matching how they are scored:
                a field the page never gave has nothing to report. */}
            {score.form.expected > 0 && <> · forma {pct(score.form.f1)}</>}
            {score.quantity.expected > 0 && <> · volume {pct(score.quantity.f1)}</>}
          </span>
          {/*
            Shown, not withheld, and marked for what it is.
            
            A number against an unconfirmed label is exactly what tells you
            which of the two is wrong -- often the label. What it must not do
            is pass for a measurement, which is a baseline's job and needs a
            label somebody has checked.
          */}
          {/* The number says how far apart; this says where. */}
          <Button
            variant="tertiary"
            tone="brand"
            size="small"
            leadingIcon={icons.locate}
            aria-label="Comparar gabarito e leitura"
            title="Comparar gabarito e leitura, campo a campo"
            onClick={() => setDiff(true)}
          />
          {source.scoreIsDraft === true && (
            <span
              className="rounded-base bg-fill-weak text-tTiny text-text-weak px-2 py-0.5"
              title="O gabarito ainda não foi conferido — esta pontuação é provisória e não serve de baseline."
            >
              gabarito em rascunho
            </span>
          )}
        </div>
      )}

      {diff && score !== undefined && (
        <ScoreDiff rows={score.rows} onClose={() => setDiff(false)} />
      )}
    </div>
  )
}
